import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useAnchoredOverlay } from "@/hooks/useAnchoredOverlay";
import { useClickOutside } from "@/hooks/useClickOutside";
import { isoDatePart } from "@/lib/dates/isoDate";
import {
  dateRangeValidationMessage,
  formatDateRangeDayCount,
  parseDateRangeValue,
  type DateRangeValue,
} from "@/lib/forms/dateRangeField";
import { reportFieldValue } from "@/lib/forms/reportFieldValue";
import { FieldDescriptionText, FieldFooter, FieldShell } from "../../field-ui";
import type { FormFieldInputProps, FormFieldRef } from "../../types";
import { DatePickerControl } from "../date/DatePickerControl";
import { DateRangeCalendar } from "./DateRangeCalendar";

interface DateRangeInputProps extends FormFieldInputProps {
  label: string;
  required: boolean;
  withTime?: boolean;
  defaultValue?: unknown;
}

const EMPTY_RANGE: DateRangeValue = { start: "", end: "" };

function selectedDateValue(iso: string, previous: string, withTime: boolean) {
  if (!withTime) return iso;
  const time = /T(\d{2}:\d{2})$/.exec(previous)?.[1] ?? "00:00";
  return `${iso}T${time}`;
}

export const DateRangeInput = forwardRef<FormFieldRef, DateRangeInputProps>(
  (props, ref) => {
    const shellRef = useRef<HTMLDivElement>(null);
    const pickerRef = useRef<HTMLDivElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);
    const startId = useId();
    const endId = useId();
    const calendarId = useId();
    const [range, setRange] = useState<DateRangeValue>(
      () =>
        parseDateRangeValue(props.initialValue ?? props.defaultValue) ??
        EMPTY_RANGE,
    );
    const [validationState, setValidationState] = useState(true);
    const [validationMessage, setValidationMessage] = useState<string | null>(
      null,
    );
    const [open, setOpen] = useState(false);
    const [selectingEnd, setSelectingEnd] = useState(false);
    const withTime = Boolean(props.withTime);
    const value = range.start || range.end ? JSON.stringify(range) : "";

    const commitRange = (next: DateRangeValue) => {
      const serialized = next.start || next.end ? JSON.stringify(next) : "";
      setRange(next);
      setValidationState(true);
      setValidationMessage(null);
      reportFieldValue(
        props.fieldName,
        serialized && !dateRangeValidationMessage(serialized, withTime, true)
          ? serialized
          : "",
        props.onValueChange,
      );
    };

    const updateRange = (part: keyof DateRangeValue, nextValue: string) => {
      commitRange({ ...range, [part]: nextValue });
    };

    const openFor = (part: keyof DateRangeValue) => {
      setSelectingEnd(part === "end" && Boolean(range.start));
      setOpen(true);
    };

    const selectDate = (iso: string) => {
      if (!selectingEnd || !range.start || iso < isoDatePart(range.start)) {
        commitRange({
          start: selectedDateValue(iso, range.start, withTime),
          end: "",
        });
        setSelectingEnd(true);
        return;
      }
      commitRange({
        ...range,
        end: selectedDateValue(iso, range.end, withTime),
      });
      setSelectingEnd(false);
      setOpen(false);
    };

    const close = useCallback(() => setOpen(false), []);
    useClickOutside(pickerRef, close, open, overlayRef);
    useAnchoredOverlay({
      open,
      triggerRef: pickerRef,
      overlayRef,
      align: "start",
      matchTriggerWidth: false,
      minHeight: 310,
    });

    useEffect(() => {
      if (!open) return;
      const onKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Escape") setOpen(false);
      };
      document.addEventListener("keydown", onKeyDown);
      return () => document.removeEventListener("keydown", onKeyDown);
    }, [open]);

    const validate = () => {
      const message = dateRangeValidationMessage(
        value,
        withTime,
        props.required,
      );
      setValidationMessage(message);
      setValidationState(message == null);
      return message == null;
    };

    const clear = () => {
      commitRange(EMPTY_RANGE);
      setSelectingEnd(false);
      setOpen(false);
    };

    useImperativeHandle(ref, () => ({
      setValidationState,
      value,
      fieldId: props.fieldId,
      fieldName: props.fieldName,
      scrollToElement: (options) => {
        shellRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
          ...options,
        });
      },
      validate,
      clear,
    }));

    return (
      <FieldShell ref={shellRef}>
        <fieldset
          onBlur={(event) => {
            if (event.currentTarget.contains(event.relatedTarget as Node))
              return;
            if (overlayRef.current?.contains(event.relatedTarget as Node))
              return;
            if (range.start || range.end) validate();
          }}
        >
          {props.label ? (
            <legend className="form-field-label mb-2 font-onest text-sm font-medium text-slate-700">
              {props.label}
              {props.required ? (
                <span className="text-red-500" aria-hidden="true">
                  {" "}
                  *
                </span>
              ) : null}
              <FieldDescriptionText className="mt-0.5 font-normal" />
            </legend>
          ) : (
            <>
              <legend className="sr-only">
                {props.accessibleName || "Интервал дат"}
              </legend>
              <FieldDescriptionText className="mb-2" />
            </>
          )}
          <div ref={pickerRef}>
            <div
              className="grid gap-2"
              style={{
                gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${withTime ? "18rem" : "14rem"}), 1fr))`,
              }}
            >
              <div className="min-w-0">
                <label
                  htmlFor={startId}
                  className="mb-1 block font-onest text-xs text-slate-600"
                >
                  С
                </label>
                <DatePickerControl
                  id={startId}
                  value={range.start}
                  onChange={(next) => updateRange("start", next)}
                  withTime={withTime}
                  invalid={!validationState}
                  calendarLabel="Открыть календарь С"
                  calendarOpen={open}
                  calendarId={calendarId}
                  onCalendarToggle={() => openFor("start")}
                  onDateFocus={() => openFor("start")}
                  timeLabel="Время С"
                />
              </div>
              <div className="min-w-0">
                <label
                  htmlFor={endId}
                  className="mb-1 block font-onest text-xs text-slate-600"
                >
                  По
                </label>
                <DatePickerControl
                  id={endId}
                  value={range.end}
                  onChange={(next) => updateRange("end", next)}
                  withTime={withTime}
                  invalid={!validationState}
                  calendarLabel="Открыть календарь По"
                  calendarOpen={open}
                  calendarId={calendarId}
                  onCalendarToggle={() => openFor("end")}
                  onDateFocus={() => openFor("end")}
                  timeLabel="Время По"
                />
              </div>
            </div>
            {formatDateRangeDayCount(range) ? (
              <p
                className="mt-2 font-onest text-xs text-slate-600"
                aria-live="polite"
              >
                {formatDateRangeDayCount(range)}
              </p>
            ) : null}
          </div>
        </fieldset>
        {open
          ? createPortal(
              <div
                ref={overlayRef}
                id={calendarId}
                role="dialog"
                aria-label="Выбор интервала дат"
                data-admin-overlay=""
                onMouseDown={(event) => event.preventDefault()}
                className="fixed z-50 w-[36rem] max-w-[calc(100vw-1rem)] rounded-xl border border-slate-200 bg-white p-3 shadow-lg"
              >
                <DateRangeCalendar
                  start={range.start}
                  end={range.end}
                  selectingEnd={selectingEnd}
                  onSelect={selectDate}
                  onClear={() => {
                    commitRange(EMPTY_RANGE);
                    setSelectingEnd(false);
                  }}
                />
              </div>,
              document.body,
            )
          : null}
        <FieldFooter>
          {!validationState ? (
            <span className="form-field-error text-red-600" role="alert">
              {validationMessage ?? "Укажите корректный интервал дат"}
            </span>
          ) : null}
        </FieldFooter>
      </FieldShell>
    );
  },
);

DateRangeInput.displayName = "DateRangeInput";
